import { dbAdd, dbDelete, dbGetAll, dbGetById, dbPut } from '../indexedDb.ts';

export interface LocalTransaction {
  id: number;
  description: string;
  amount: number;
  type: 'income' | 'expense';
  due_date: string; // YYYY-MM-DD
  paid_date?: string;
  is_paid: boolean;
  account_id: number;
  destination_account_id?: number;
  category_id: number;
  created_at: string;
  updated_at?: string;
  // Recurring fields
  recurring_transaction_id?: number;
  is_recurring?: boolean;
  installment_number?: number;
  // Sync fields
  server_id?: number;
  last_synced_at?: string;
  needs_sync?: boolean;
}

export async function listTransactions(competence?: string): Promise<LocalTransaction[]> {
  const all = await dbGetAll<LocalTransaction>('transactions');
  if (!competence) return all;
  return all.filter(t => competenceOf(t.due_date) === competence);
}

export function getTransactionById(id: number) {
  return dbGetById<LocalTransaction>('transactions', id);
}

export function competenceOf(date: string): string {
  const [y, m] = date.split('-');
  return `${m}/${y}`;
}

export async function createTransaction(data: Omit<LocalTransaction, 'id'|'created_at'|'updated_at'|'server_id'|'last_synced_at'|'needs_sync'>) {
  const now = new Date().toISOString();
  return dbAdd('transactions', { ...data, created_at: now, needs_sync: true } as any);
}

export async function updateTransaction(id: number, data: Partial<LocalTransaction>) {
  const current = await dbGetById<LocalTransaction>('transactions', id);
  if (!current) throw new Error('Transaction not found');
  const updated = {
    ...current,
    ...data,
    id,
    updated_at: data.updated_at ?? new Date().toISOString(),
    needs_sync: data.needs_sync ?? true
  };
  await dbPut('transactions', updated);
  return updated;
}

export async function removeTransaction(id: number) { return dbDelete('transactions', id); }

export async function markPaid(id: number) {
  const current = await dbGetById<LocalTransaction>('transactions', id);
  if (!current) throw new Error('Transaction not found');
  current.is_paid = true;
  current.paid_date = current.paid_date || new Date().toISOString().slice(0,10);
  current.updated_at = new Date().toISOString();
  current.needs_sync = true;
  await dbPut('transactions', current);
  return current;
}

export async function markUnpaid(id: number) {
  const current = await dbGetById<LocalTransaction>('transactions', id);
  if (!current) throw new Error('Transaction not found');
  current.is_paid = false;
  current.paid_date = '';
  current.updated_at = new Date().toISOString();
  current.needs_sync = true;
  await dbPut('transactions', current);
  return current;
}

export async function getTransactionsNeedingSync(): Promise<LocalTransaction[]> {
  const all = await dbGetAll<LocalTransaction>('transactions');
  return all.filter(t => t.needs_sync === true);
}

export async function markSynced(id: number, serverId: number) {
  const current = await dbGetById<LocalTransaction>('transactions', id);
  if (!current) throw new Error('Transaction not found');
  current.server_id = serverId;
  current.last_synced_at = new Date().toISOString();
  current.needs_sync = false;
  await dbPut('transactions', current);
  return current;
}


